import { api } from "./api";

export interface Lesson {
  _id: string;
  title: string;
  type: "video" | "text" | "code" | "quiz" | "assignment" | "mixed";
  duration: number;
  order: number;
  isPreview: boolean;
  isPublished: boolean;
  videoUrl?: string;
  content?: string;
  bullets?: string | string[];
  topics?: any[];
  links?: { text: string; url: string }[];
  codeBlocks?: { language: string; code: string }[];
  images?: { url: string; caption?: string }[];
}

export interface Module {
  _id: string;
  title: string;
  description: string;
  order: number;
  isPublished: boolean;
  lessons: Lesson[];
  totalDuration: number;
  totalLessons: number;
  hasQuiz?: boolean;
  quiz?: any;
}

const buildQueryString = (filters?: Record<string, any>): string => {
  if (!filters) return "";
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  });
  return params.toString();
};

export const modulesApi = {
  // Get all modules for a course
  getCourseModules: (courseId: string) => {
    return api.get<{ success: boolean; count: number; data: Module[] }>(
      `/modules/course/${courseId}`
    );
  },

  // Get single module
  getModule: (moduleId: string) => {
    return api.get<{ success: boolean; data: Module }>(`/modules/${moduleId}`);
  },

  // Create module
  createModule: (courseId: string, data: { title: string; description?: string; order?: number }) => {
    return api.post<{ success: boolean; message: string; data: Module }>(
      `/modules/course/${courseId}`,
      data
    );
  },

  // Update module
  updateModule: (moduleId: string, data: { title?: string; description?: string; order?: number; isPublished?: boolean }) => {
    return api.put<{ success: boolean; message: string; data: Module }>(
      `/modules/${moduleId}`,
      data
    );
  },

  // Delete module
  deleteModule: (moduleId: string) => {
    return api.delete<{ success: boolean; message: string }>(`/modules/${moduleId}`);
  },

  // Reorder modules
  reorderModules: (courseId: string, moduleOrders: { moduleId: string; order: number }[]) => {
    return api.put<{ success: boolean; message: string }>(
      `/modules/course/${courseId}/reorder`,
      { moduleOrders }
    );
  },

  // Get lessons for a module
  getModuleLessons: (moduleId: string) => {
    return api.get<{ success: boolean; count: number; data: Lesson[] }>(
      `/lessons/modules/${moduleId}`
    );
  },

  // Get single lesson
  getLesson: (lessonId: string) => {
    return api.get<{ success: boolean; data: Lesson }>(`/lessons/${lessonId}`);
  },

  // Create lesson
  createLesson: (moduleId: string, data: {
    title: string;
    type: "video" | "text" | "code" | "quiz" | "assignment" | "mixed";
    duration?: number;
    order?: number;
    isPreview?: boolean;
    isPublished?: boolean;
    videoUrl?: string;
    content?: string;
    bullets?: string;
    topics?: any;
    paragraphs?: any;
    links?: any;
    codeBlocks?: any;
  }) => {
    return api.post<{ success: boolean; message: string; data: Lesson }>(
      `/lessons/modules/${moduleId}`,
      data
    );
  },

  // Update lesson
  updateLesson: (lessonId: string, data: {
    title?: string;
    type?: "video" | "text" | "code" | "quiz" | "assignment" | "mixed";
    duration?: number;
    order?: number;
    isPreview?: boolean;
    isPublished?: boolean;
    videoUrl?: string;
    content?: string;
    bullets?: string;
    topics?: any;
    paragraphs?: any;
    links?: any;
    codeBlocks?: any;
  }) => {
    return api.put<{ success: boolean; message: string; data: Lesson }>(
      `/lessons/${lessonId}`,
      data
    );
  },

  // Delete lesson
  deleteLesson: (lessonId: string) => {
    return api.delete<{ success: boolean; message: string }>(`/lessons/${lessonId}`);
  },

  // Reorder lessons
  reorderLessons: (moduleId: string, lessonOrders: { lessonId: string; order: number }[]) => {
    return api.put<{ success: boolean; message: string }>(
      `/lessons/modules/${moduleId}/reorder`,
      { lessonOrders }
    );
  },

  // Quiz methods
  createQuiz: (moduleId: string, data: {
    title: string;
    description?: string;
    timeLimit?: number;
    passingScore?: number;
    questions?: any[];
    isCertificateExam?: boolean;
    certificateTemplate?: string;
    examDuration?: number;
    minQuestions?: number;
  }) => {
    return api.post<{ success: boolean; message: string; data: any }>(
      `/quizzes/${moduleId}/quiz`,
      data
    );
  },

  getModuleQuiz: (moduleId: string) => {
    return api.get<{ success: boolean; data: any }>(`/quizzes/${moduleId}/quiz`);
  },

  updateQuiz: (quizId: string, data: {
    title?: string;
    description?: string;
    timeLimit?: number;
    passingScore?: number;
    isCertificateExam?: boolean;
    certificateTemplate?: string;
    examDuration?: number;
    minQuestions?: number;
  }) => {
    return api.put<{ success: boolean; message: string; data: any }>(
      `/quizzes/${quizId}`,
      data
    );
  },

  getCertificateExams: () => {
    return api.get<{ success: boolean; data: any[] }>("/quizzes/certificate-exams");
  },

  deleteQuiz: (moduleId: string) => {
    return api.delete<{ success: boolean; message: string }>(`/quizzes/${moduleId}/quiz`);
  },

  addQuizQuestion: (quizId: string, data: {
    questionText: string;
    type: "multiple-choice" | "true-false" | "short-answer";
    options?: { text: string; isCorrect: boolean }[];
    correctOptionId?: string;
    points?: number;
    explanation?: string;
  }) => {
    return api.post<{ success: boolean; message: string; data: any }>(
      `/quizzes/${quizId}/questions`,
      data
    );
  },

  deleteQuizQuestion: (quizId: string, questionId: string) => {
    return api.delete<{ success: boolean; message: string }>(
      `/quizzes/${quizId}/questions/${questionId}`
    );
  },
};